import React, { useMemo } from "react";
import { Dropdown, Tooltip, message } from "antd";
import {
  AppstoreOutlined,
  CheckOutlined,
  DownOutlined,
  SettingOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useProject } from "../../context/ProjectContext";
import "../../styles/projectSwitcher.css";

/**
 * ProjectSwitcher — sits under the sidebar logo.
 *
 * Switching is transient (it never silently changes your default). "Set as my
 * default" persists the project you land in at login; the current default is
 * marked with a filled star.
 */
function ProjectSwitcher({ collapsed = false }) {
  const navigate = useNavigate();
  const {
    projects,
    activeProject,
    activeProjectId,
    defaultProjectId,
    isAdmin,
    switchProject,
    setAsDefaultProject,
  } = useProject();

  const activeProjects = useMemo(
    () => (projects || []).filter((p) => p.active),
    [projects],
  );

  const handleSetDefault = async () => {
    if (!activeProjectId) return;
    try {
      await setAsDefaultProject(activeProjectId);
      message.success(`"${activeProject?.name}" is now your default project`);
    } catch {
      message.error("Could not set your default project");
    }
  };

  const dateWindow = useMemo(() => {
    if (!activeProject) return null;
    const start = activeProject.startDate?.split(" ")[0];
    const end = activeProject.endDate?.split(" ")[0];
    if (!start && !end) return null;
    return `${start || "—"} → ${end || "—"}`;
  }, [activeProject]);

  // Past its end date → show an "Ended" chip. Dates are dd-MM-yyyy HH:mm:ss;
  // parse the date part manually to avoid a dayjs plugin dependency here.
  const isEnded = useMemo(() => {
    const end = activeProject?.endDate?.split(" ")[0];
    if (!end) return false;
    const [dd, mm, yyyy] = end.split("-").map(Number);
    if (!dd || !mm || !yyyy) return false;
    const endOfDay = new Date(yyyy, mm - 1, dd, 23, 59, 59).getTime();
    return endOfDay < Date.now();
  }, [activeProject]);

  const items = [
    {
      key: "heading",
      type: "group",
      label: <span className="ps-menu-heading">Switch project</span>,
      children: activeProjects.map((p) => ({
        key: p.projectId,
        label: (
          <div className="ps-menu-item">
            <span className="ps-menu-check">
              {p.projectId === activeProjectId && <CheckOutlined />}
            </span>
            <span className="ps-menu-name">{p.name}</span>
            {p.projectId === defaultProjectId && (
              <Tooltip title="Your default project">
                <StarFilled className="ps-menu-star" />
              </Tooltip>
            )}
          </div>
        ),
      })),
    },
    { type: "divider" },
    {
      key: "set-default",
      disabled: !activeProjectId || activeProjectId === defaultProjectId,
      label: (
        <span className="ps-menu-action">
          <StarOutlined />
          {activeProjectId === defaultProjectId
            ? "Already your default"
            : "Set as my default"}
        </span>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: "manage",
            label: (
              <span className="ps-menu-action">
                <SettingOutlined />
                Manage projects
              </span>
            ),
          },
        ]
      : []),
  ];

  const onClick = ({ key }) => {
    if (key === "set-default") return handleSetDefault();
    if (key === "manage") return navigate("/admin/projects");
    switchProject(key);
  };

  const label = activeProject?.name || "No project";

  if (collapsed) {
    return (
      <div className="ps-wrap ps-wrap--collapsed">
        <Dropdown
          menu={{ items, onClick }}
          trigger={["click"]}
          placement="bottomLeft"
        >
          <Tooltip title={label} placement="right">
            <button type="button" className="ps-trigger ps-trigger--collapsed">
              <AppstoreOutlined />
            </button>
          </Tooltip>
        </Dropdown>
      </div>
    );
  }

  return (
    <div className="ps-wrap">
      <Dropdown
        menu={{ items, onClick }}
        trigger={["click"]}
        placement="bottomLeft"
      >
        <button type="button" className="ps-trigger">
          <span className="ps-icon">
            <AppstoreOutlined />
          </span>
          <span className="ps-text">
            <span className="ps-label">Project</span>
            <span className="ps-name-row">
              <span className="ps-name" title={label}>
                {label}
              </span>
              {isEnded && <span className="ps-ended">Ended</span>}
            </span>
            {dateWindow && <span className="ps-dates">{dateWindow}</span>}
          </span>
          <DownOutlined className="ps-caret" />
        </button>
      </Dropdown>
    </div>
  );
}

export default ProjectSwitcher;
